import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { CONFIG } from '../config'
import { authService } from '../services/auth.service'
import { notificationService } from '../services/notification.service'

// HTTP link to GraphQL endpoint
const httpLink = createHttpLink({
  uri: `${CONFIG.API.url}/graphql`
})

// Context link for headers (auth + domain + branch)
const contextLink = setContext(async (_, { headers }) => {
  const token = authService.getValidToken()
  const branchId = authService.getBranchId()
  const isLocalhost = window.location.hostname === 'localhost'
  
  return {
    headers: {
      ...headers,
      // Add authorization token if available (backend will validate)
      ...(token && { authorization: `Bearer ${token}` }),
      // Add branch ID header if available
      ...(branchId && { 'x-branch-id': branchId }),
      // Add domain header only for localhost development
      ...(isLocalhost && { 'x-client-domain': CONFIG.CLIENT.domain }),
    }
  }
})

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      
      // Handle authentication errors from GraphQL
      if (extensions?.code === 'UNAUTHENTICATED' || 
          message.includes('Unauthorized') || 
          message.includes('Invalid token') ||
          message.includes('token revoked') ||
          message.includes('token invalid') ||
          message.toLowerCase().includes('authentication')) {
        authService.handleUnauthenticated()
        return
      }
      
      if (CONFIG.ENV.isDevelopment) {
        notificationService.error(`GraphQL Error: ${message}`)
      }
    })
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)
    
    // Handle specific network errors
    if ('statusCode' in networkError) {
      switch (networkError.statusCode) {
        case 401:
          authService.handleUnauthenticated()
          break
        case 403:
          notificationService.error('Access forbidden')
          break
        case 500:
          notificationService.error('Server error occurred')
          break
        default:
          notificationService.error('Network error occurred')
      }
    }
  }
})

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, contextLink, httpLink]),
  cache: new InMemoryCache({
    // Disable normalization by default - prevents cache merging issues with nested objects
    // Objects will be stored inline with their parents instead of being normalized
    dataIdFromObject: () => undefined,
    typePolicies: {
      Query: {
        fields: {
          // Exception: Custom merge function for orders pagination (only field that needs merging)
          orders: {
            keyArgs: ['filters'],
            merge(existing, incoming, { args }) {
              if (!existing) return incoming
              const offset = args?.offset || 0
              if (offset === 0) return incoming // Fresh data
              
              // Merge for pagination
              return {
                ...incoming,
                edges: [...(existing.edges || []), ...(incoming.edges || [])]
              }
            }
          }
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
    },
  },
}) 